import React, { useState, useEffect, Dispatch, SetStateAction } from "react";
import { IActionHandler, MessageDisplayType, IFetchResponse } from "@preveil-api";
import {
  Account, useAppSelector, useAppDispatch, Attachment, DirectoryEntity, useFetchDirectory, useFetchLink, TreeItem, TreeItemType, buildDriveTreeFromRoot,
  DriveConstants, GlobalErrorMessages, Message, MessageHandlerDisplayType, MessageAnchors, FilesyncStatusCode, useCreateDirectoryMutation, DriveSuccessMessages, validateFileFolderName, DriveEntryType,
  CreateDirectoryApiResponse, DriveErrorMessages
} from "src/common";
import { Loading, Tree } from "src/components";
import { driveActions, uiActions } from "src/store";
import { RootState } from "src/store/configureStore";


type AllProps = {
    current_account: Account;
    current_attachment?: Attachment;
    setDestination: Dispatch<SetStateAction<TreeItem | undefined>>;
}

function DriveTabContentComponent(props: AllProps) {
    const { current_account, setDestination } = props;
    const dispatch = useAppDispatch();
    const root_info = useAppSelector((state: RootState) => state.drive.root_info);
    const [active, setActive] = useState<string | undefined>();
    const [root_tree, setRootTree] = useState<TreeItem>();
    const [tree_item, setTreeItem] = useState<TreeItem>();
    const [fetching_children, setFetchingChildren] = useState<boolean>(false);
    const [pending_new_folder_id, setPendingNewFolderId] = useState<string | undefined>();
    const [createDirectory] = useCreateDirectoryMutation();
    const { directory, error: dir_error, fetchDirectory, destroyFetchDirectory } = useFetchDirectory(current_account, false, true);
    const { directory_id, directory_entity, error: link_error, fetchLink, resetLink } = useFetchLink(current_account);
    // Description: On load of Thread View - build tree for current directory as well as root tree
    useEffect(() => {
        !!root_info && fetchDirectory(root_info.collection_id, root_info.id);
    }, [root_info]);

    // Description: Build tree structure on fetch Directory success or error
    useEffect(() => {
        dir_error ? handleFetchDirectoryError() :
            (!!directory && handleFetchDirectorySuccess(directory));
    }, [directory, dir_error]);

    // Description: Build tree structure after fetch LINK_ENTITY success or error
    useEffect(() => {
        if (!!link_error) {
            handleFetchDirectoryError();
        } else if (!!directory_id) {
            !!tree_item?.linked_collection_id ?
                fetchDirectory(tree_item.linked_collection_id, directory_id) :
                handleFetchDirectoryError();
        } else if (!!directory_entity) {
            handleFetchDirectorySuccess(directory_entity);
        }
    }, [directory_id, directory_entity, link_error]);

    // Description: Attachment Modal Requests
    const AttachmentModalRequests = {
        // Description: Sets the active item as well as the destination (on click of a node)
        handleTreeNodeClick: (item: TreeItem) => {
            if (!!item.type) {
                !!item.id ? setActive(item.id) : setActive(undefined);
                setDestination(item);
            }
        },
        // Description: On click of the chevron, fetches the nodes children through fetch directory
        handleFetchTreeNodeChildren: (item: TreeItem) => {
            const _collection_id = item?.collection_id;
            if (!!item.id && !!_collection_id) {
                setTreeItem(item);
                item.children_loading = true;
                setFetchingChildren(true);
                !!item.linked_collection_id ? fetchLink(_collection_id, item.id) :
                    fetchDirectory(_collection_id, item.id);
            } else {
                handleFetchDirectoryError();
            }
        },
        // Description: Create a new folder under the given parent folder
        handleTreeNodeAddChild: async (params: { folder: TreeItem; name: string }) => {
            const { folder, name } = params;
            setFetchingChildren(true);
            const new_folder = await createFolderForDownload(folder, name);
            if (!!new_folder) {
                dispatch(uiActions.handleSetMessage(new Message(DriveSuccessMessages.create_success.replace(MessageAnchors.file_name, new_folder.name || "folder"))));
                setPendingNewFolderId(new_folder.id);
                // Refresh the parent folder to show the newly created child
                const _collection_id = folder.collection_id;
                if (!!folder.id && !!_collection_id) {
                    setTreeItem(folder);
                    folder.children_loading = true;
                    !!folder.linked_collection_id ? fetchLink(_collection_id, folder.id) :
                        fetchDirectory(_collection_id, folder.id);
                } else {
                    setFetchingChildren(false);
                }
            } else {
                setFetchingChildren(false);
            }
        },
        handlePageErrorMessage: (params: { message: string; stack?: any, display_type?: MessageDisplayType }) => {
            dispatch(
                uiActions.handleRequestErrors(
                    new Message(params.message, params.display_type || MessageHandlerDisplayType.logger),
                    params.stack,
                ),
            );
        },
    };

    //  Description: Handle all actions from Children forms 
    function handlePageActions(action_obj: IActionHandler) {
        const callback = `handle${action_obj.actionType}`;
        if ((AttachmentModalRequests as any)[callback] instanceof Function) {
            (AttachmentModalRequests as any)[callback](action_obj.params);
        } else {
            const message = GlobalErrorMessages.no_handler_found.replace(
                MessageAnchors.actionType,
                action_obj.actionType,
            );
            AttachmentModalRequests.handlePageErrorMessage({ message, stack: action_obj });
        }
    }

    // Description: Handle callback from FetchDirectory
    function handleFetchDirectorySuccess(_directory: DirectoryEntity) {
        dispatch(driveActions.setCurrentDirectorySuccess(_directory));
        // If the custom root tree is not populated yet, it populates it here
        if (!root_tree) {
            const children = buildDriveTreeFromRoot(_directory, DriveConstants.ROOT_DIRECTORY);
            const root = {
                id: _directory.id,
                collection_id: _directory.collection_id,
                name: DriveConstants.ROOT_DIRECTORY,
                type: TreeItemType.folder,
                path: DriveConstants.ROOT_DIRECTORY,
                children: !!children && children.length > 0 ? children : undefined,
                expanded: true
            };
            setRootTree(root);
        } else {
            // NOTE: sets the tree_items children when a node is opened (it fetches the children of the node)
            if (!!tree_item) {
                const children = buildDriveTreeFromRoot(_directory, tree_item.path);
                if (!!children && children.length > 0) {
                    tree_item.children = children;
                    // If a new folder was just created, find and select it
                    if (!!pending_new_folder_id) {
                        const newly_created_folder = children.find((child: TreeItem) => child.id === pending_new_folder_id);
                        if (!!newly_created_folder) {
                            setActive(newly_created_folder.id);
                            setDestination(newly_created_folder);
                            setPendingNewFolderId(undefined);
                        }
                    }
                }
                tree_item.children_loading = false;
            }
            setFetchingChildren(false);
        }
        destroyFetchDirectory();
        resetLink();
    }

    // Description: Handle FetchDirectory Errors
    function handleFetchDirectoryError() {
        destroyFetchDirectory();
        resetLink();
        if (!!tree_item) {
            tree_item.children_loading = false;
        }
        setFetchingChildren(false);
    }

    // Functions to handle Create new directory: 
    // ------------------------------------------------
    // Description: Create a new folder under the specified parent (callback pattern)
    async function createFolderForDownload(parent_folder: TreeItem, folder_name: string): Promise<CreateDirectoryApiResponse | null> {
        const trimmed_name = folder_name.trim();
        // NOTE: Validate the folder name
        const validation_error = validateFileFolderName(trimmed_name, DriveEntryType.DIR);
        if (!!validation_error) {
            dispatch(uiActions.handleRequestErrors(new Message(validation_error)));
            return null;
        }

        // NOTE: Validate parent folder
        if (!parent_folder.collection_id || !parent_folder.id) {
            dispatch(uiActions.handleRequestErrors(new Message(DriveErrorMessages.default)));
            return null;
        }

        // NOTE: Call the API to create the directory
        return await createDirectory({
            user_id: current_account.user_id,
            directory_id: !!parent_folder.linked_root_directory_id ? parent_folder.linked_root_directory_id : parent_folder.id,
            collection_id: !!parent_folder.linked_collection_id ? parent_folder.linked_collection_id : parent_folder.collection_id,
            name: trimmed_name,
        }).unwrap()
            .catch((error: IFetchResponse) => {
                const error_string = typeof error.data === "string" ? error.data : JSON.stringify(error.data ?? "");
                const is_view_only = error_string.includes(FilesyncStatusCode.UNAUTHORIZED) || error_string.includes("writerKey=nil");
                const is_conflict = error_string.includes(FilesyncStatusCode.CONFLICT);
                const msg = is_conflict ? DriveErrorMessages.existing_name.replace(MessageAnchors.file_name, trimmed_name) :
                    is_view_only ? DriveErrorMessages.no_access_permission :
                        DriveErrorMessages.error_creating_folder.replace(MessageAnchors.file_name, trimmed_name);
                dispatch(uiActions.handleRequestErrors(new Message(msg), error));
                return null;
            });
    }

    return <div className="move-folders">
        {
            !!root_tree ?
                <Tree
                    className="move-tree"
                    data={[root_tree]}
                    readonly
                    show_create_folder
                    active={active}
                    fetching_children={fetching_children}
                    drive={true}
                    handleAction={handlePageActions} /> :
                <Loading className="in-place" />
        }
    </div>;
}

export default React.memo(DriveTabContentComponent);